import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../api';
import {
  PageHeader, Loading, ErrorBanner, Empty, Badge, Modal, ui, fmtDate, asList,
} from '../components/adminUi';

const STATUS_TONE = { open: 'amber', pending: 'amber', resolved: 'green', closed: 'gray', rejected: 'red' };

export default function DisputesPage() {
  const { auth } = useAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [decision, setDecision] = useState('');
  const [resolution, setResolution] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const res = await apiFetch('/api/admin/disputes', auth.token);
      if (!res.ok) throw new Error();
      setItems(asList(await res.json()));
    } catch {
      setItems([]);
      setFetchError('Disputlarni yuklashda xatolik. Sahifani yangilang.');
    } finally {
      setLoading(false);
    }
  }, [auth.token]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (row) => {
    setFormError('');
    setDecision('');
    setResolution('');
    setDetail({ ...row, __loading: true });
    setDetailLoading(true);
    try {
      const res = await apiFetch(`/api/admin/disputes/${row.id}`, auth.token);
      if (res.ok) {
        setDetail(await res.json());
      } else {
        // Fall back to the list row if detail endpoint has no richer data.
        setDetail(row);
      }
    } catch {
      setDetail(row);
    } finally {
      setDetailLoading(false);
    }
  };

  const isResolved = (row) => ['resolved', 'closed', 'rejected'].includes(row?.status);

  const resolve = async () => {
    if (!detail) return;
    setFormError('');
    if (!resolution.trim()) { setFormError('Yechim matni majburiy'); return; }
    setSaving(true);
    try {
      const res = await apiFetch(`/api/admin/disputes/${detail.id}/resolve`, auth.token, {
        method: 'POST',
        body: JSON.stringify({
          resolution: resolution.trim(),
          ...(decision ? { decision } : {}),
        }),
      });
      if (!res.ok) {
        const dd = await res.json().catch(() => ({}));
        setFormError(dd.message || 'Hal qilishda xatolik yuz berdi.');
        return;
      }
      setDetail(null);
      await load();
    } catch {
      setFormError('Tarmoq xatosi. Qayta urinib ko\'ring.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <PageHeader title="Disputlar" subtitle="Nizolarni ko'rib chiqish va hal qilish">
        <button style={ui.pagerBtn} onClick={load}>↻ Yangilash</button>
      </PageHeader>

      <ErrorBanner>{fetchError}</ErrorBanner>

      {loading ? (
        <Loading />
      ) : items.length === 0 ? (
        <Empty icon="⚖️" title="Hali dispute yo'q" hint="Ochilgan nizolar bu yerda ko'rinadi." />
      ) : (
        <div style={ui.tableWrap}>
          <table style={ui.table}>
            <thead>
              <tr>
                <th style={{ ...ui.th, width: '48px' }}>#</th>
                <th style={ui.th}>Mavzu / sabab</th>
                <th style={ui.th}>Holat</th>
                <th style={ui.th}>Yaratilgan</th>
                <th style={{ ...ui.th, textAlign: 'right' }}>Amallar</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} style={ui.tr}>
                  <td style={{ ...ui.td, color: 'var(--text-faint)' }}>{r.id}</td>
                  <td style={{ ...ui.td, color: 'var(--text)' }}>
                    {r.subject || r.reason || r.title || r.description || `Dispute #${r.id}`}
                  </td>
                  <td style={ui.td}>
                    <Badge tone={STATUS_TONE[r.status] || 'gray'}>{r.status || '—'}</Badge>
                  </td>
                  <td style={ui.td}>{fmtDate(r.createdAt)}</td>
                  <td style={{ ...ui.td, textAlign: 'right' }}>
                    <button style={{ ...ui.btnSm, ...ui.btnBlue }} onClick={() => openDetail(r)}>
                      Batafsil
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <Modal title={`Dispute #${detail.id}`} onClose={() => setDetail(null)} maxWidth="500px">
          {detailLoading || detail.__loading ? (
            <Loading />
          ) : (
            <>
              <Row label="Mavzu" value={detail.subject || detail.title || detail.reason} />
              <Row label="Tavsif" value={detail.description || detail.message} />
              <Row label="Holat" value={detail.status} />
              <Row label="Buyurtma ID" value={detail.orderId ?? detail.taskId} />
              <Row label="Yaratilgan" value={fmtDate(detail.createdAt, true)} />
              {detail.resolution && <Row label="Yechim" value={detail.resolution} />}

              {isResolved(detail) ? (
                <div style={{ ...ui.infoBanner, marginTop: '12px' }}>
                  Bu dispute allaqachon hal qilingan.
                </div>
              ) : (
                <>
                  <div style={{ height: '8px' }} />
                  <div style={ui.field}>
                    <label style={ui.label}>Qaror (decision)</label>
                    <select
                      style={ui.select}
                      value={decision}
                      onChange={(e) => setDecision(e.target.value)}
                      disabled={saving}
                    >
                      <option value="">Tanlang (ixtiyoriy)</option>
                      <option value="client">Mijoz foydasiga</option>
                      <option value="provider">Usta foydasiga</option>
                      <option value="refund">Pulni qaytarish</option>
                      <option value="rejected">Rad etish</option>
                    </select>
                  </div>
                  <div style={ui.field}>
                    <label style={ui.label}>Yechim matni <span style={ui.req}>*</span></label>
                    <textarea
                      style={ui.textarea}
                      rows={3}
                      value={resolution}
                      onChange={(e) => setResolution(e.target.value)}
                      placeholder="Qanday hal qilinganini yozing"
                      disabled={saving}
                    />
                  </div>
                  {formError && <div style={ui.formError}>{formError}</div>}
                  <div style={ui.modalActions}>
                    <button style={ui.cancelBtn} onClick={() => setDetail(null)} disabled={saving}>
                      Yopish
                    </button>
                    <button
                      style={{ ...ui.saveBtn, ...(saving ? ui.btnDisabled : {}) }}
                      onClick={resolve}
                      disabled={saving}
                    >
                      {saving ? 'Saqlanmoqda...' : 'Hal qilish'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </Modal>
      )}
    </AdminLayout>
  );
}

function Row({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: '12px',
      padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: '14px',
    }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ color: 'var(--text)', fontWeight: '500', textAlign: 'right' }}>{value}</span>
    </div>
  );
}
